import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Header from './components/Header';
import ChatInterface from './components/ChatInterface';
import ResultsPanel from './components/ResultsPanel';
import ConversationHistory from './components/ConversationHistory';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { Loader2, CheckCircle, XCircle, BookOpen, PanelLeft, PanelLeftClose, RefreshCw } from 'lucide-react';
import api from './services/api';
import { API_BASE_URL } from './utils/api';

// Main app content component - ALL HOOKS CALLED CONSISTENTLY
function AppContent() {
  // ALL HOOKS MUST BE CALLED EVERY RENDER - NO CONDITIONS
  const { user, token, loading, isAuthenticated, login, updateToken } = useAuth();
  const [qbConnected, setQbConnected] = useState(false);
  const [qbStatus, setQbStatus] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [latestResult, setLatestResult] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [showConversationHistory, setShowConversationHistory] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [qbChecking, setQbChecking] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [oauthMessage, setOauthMessage] = useState(null);
  const chatInputRef = useRef(null);
  
  // Ref to track if we're currently checking QB connection
  const isCheckingQB = useRef(false);

  // All callback hooks - always called
  const checkQBConnection = useCallback(async (retries = 3) => {
    if (!token || !isAuthenticated) return;
    
    // Prevent multiple simultaneous calls
    if (isCheckingQB.current) {
      console.log('QB connection check already in progress, skipping...');
      return;
    }
    
    isCheckingQB.current = true;
    setQbChecking(true); // Show loading state
    let attempt = 0;
    let lastError = null;
    
    try {
      while (attempt < retries) {
        try {
          const demoMode = localStorage.getItem('demo_mode');
          if (demoMode === 'true') {
            setQbConnected(true);
            setQbStatus({
              connected: true,
              company_name: 'Demo Company',
              company_id: 'demo',
              last_sync: new Date().toISOString()
            });
            setQbChecking(false);
            return;
          }

          console.log(`Checking QuickBooks connection status (attempt ${attempt + 1})...`);
          
          // Add timeout to the fetch request
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
          
          try {
            const response = await fetch('/api/quickbooks/status', {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (response.ok) {
              const data = await response.json();
              console.log('QuickBooks status response:', data);
              setQbConnected(data.connected);
              setQbStatus(data);
              if (!data.connected && data.error) {
                console.warn('QuickBooks connection error:', data.error);
              }
              setQbChecking(false);
              return; // Success, exit retry loop
            } else {
              console.error('QuickBooks status check failed:', response.status);
              setQbConnected(false);
              setQbStatus({
                connected: false,
                error: `HTTP ${response.status}: Failed to check connection`
              });
              lastError = new Error(`HTTP ${response.status}`);
            }
          } catch (error) {
            clearTimeout(timeoutId);
            lastError = error;
            if (error.name === 'AbortError') {
              console.error('QuickBooks status check timed out');
              setQbConnected(false);
              setQbStatus({
                connected: false,
                error: 'Connection check timed out. Retrying...'
              });
            } else {
              console.error('Error checking QB connection:', error);
              setQbConnected(false);
              setQbStatus({
                connected: false,
                error: `Connection error: ${error.message}. Retrying...`
              });
            }
          }
          // Wait before retrying
          attempt++;
          if (attempt < retries) {
            await new Promise(res => setTimeout(res, 2000));
          }
        } catch (error) {
          lastError = error;
          attempt++;
          if (attempt < retries) {
            await new Promise(res => setTimeout(res, 2000));
          }
        }
      }
      // If all retries failed
      setQbConnected(false);
      setQbStatus({
        connected: false,
        error: lastError ? lastError.message : 'Unknown error after retries.'
      });
    } finally {
      isCheckingQB.current = false;
      setQbChecking(false); // Hide loading state
    }
  }, [token, isAuthenticated]);

  const handleNewChat = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    setLatestResult(null);
  }, []);

  const handleGoToHome = useCallback(() => {
    handleNewChat();
  }, [handleNewChat]);

  const loadConversations = useCallback(async () => {
    if (!token) return;
    
    console.log('Loading conversations...');
    setConversationsLoading(true);
    try {
      const response = await api.getConversations();
      console.log('Conversations loaded:', response);
      setConversations(response);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [token]);

  const loadConversation = useCallback(async (conversationId) => {
    if (!token || !conversationId) return;
    
    try {
      const response = await api.getConversation(conversationId);
      // Map thinking_log into thinkingProcess for each assistant message
      const mappedMessages = (response.messages || []).map(msg => {
        if (msg.role === 'assistant') {
          // Check for thinking_log in message_data
          let thinkingLog = [];
          if (msg.message_data && msg.message_data.thinking_log) {
            thinkingLog = msg.message_data.thinking_log;
          }
          
          if (thinkingLog.length > 0) {
            return {
              ...msg,
              thinkingProcess: thinkingLog,
              thinkingProcessStatus: 'complete',
            };
          }
        }
        return msg;
      });
      setMessages(mappedMessages);
      setCurrentConversation(conversationId);
      
      // Extract analysis data from the last assistant message
      const assistantMessages = (response.messages || []).filter(msg => msg.role === 'assistant');
      if (assistantMessages.length > 0) {
        const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
        
        // Handle different message_data structures for backward compatibility
        let resultData = null;
        if (lastAssistantMessage.message_data) {
          // New format: message_data contains data, visualization, sources
          if (typeof lastAssistantMessage.message_data === 'object') {
            resultData = {
              data: lastAssistantMessage.message_data.data || lastAssistantMessage.message_data,
              visualization: lastAssistantMessage.message_data.visualization,
              sources: lastAssistantMessage.message_data.sources,
              table: lastAssistantMessage.message_data.table,
              chart: lastAssistantMessage.message_data.chart
            };
          }
        }
        
        // Also check if the message has direct data properties (older format)
        if (!resultData && (lastAssistantMessage.data || lastAssistantMessage.visualization)) {
          resultData = {
            data: lastAssistantMessage.data,
            visualization: lastAssistantMessage.visualization,
            sources: lastAssistantMessage.sources,
            table: lastAssistantMessage.table,
            chart: lastAssistantMessage.chart
          };
        }
        
        setLatestResult(resultData);
      } else {
        setLatestResult(null);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  }, [token]);

  const handleSelectConversation = useCallback((conversationId) => {
    console.log('Selecting conversation:', conversationId);
    loadConversation(conversationId);
  }, [loadConversation]);

  const handleDeleteConversation = useCallback(async (conversationId) => {
    if (!token) return;
    
    try {
      await api.deleteConversation(conversationId);
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      if (currentConversation === conversationId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  }, [token, currentConversation, handleNewChat]);

  // Replace the main streaming logic to use EventSource for /api/chat/query/stream
  const handleSendMessage = useCallback(async (message) => {
    if (!message) return;
    setChatLoading(true);
    // Add user and assistant message in a single call
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: message, timestamp: new Date() },
      {
        role: 'assistant',
        content: '',
        thinkingProcess: [],
        thinkingProcessStatus: 'in_progress',
        timestamp: new Date()
      }
    ]);

    // Get token from localStorage
    const token = localStorage.getItem('access_token');
    // Use full backend URL to avoid dev proxy buffering for streaming endpoints
    const backendBaseUrl = API_BASE_URL;
    const url = `${backendBaseUrl}/api/chat/query/stream?query=${encodeURIComponent(message)}${currentConversation ? `&conversation_id=${encodeURIComponent(currentConversation)}` : ''}${token ? `&token=${encodeURIComponent(token)}` : ''}`;

    // NOTE: If you want to use the dev proxy for streaming, you must configure it to not buffer responses.
    // For Create React App, you can use http-proxy-middleware in setupProxy.js like this:
    //
    //   const { createProxyMiddleware } = require('http-proxy-middleware');
    //   module.exports = function(app) {
    //     app.use(
    //       '/api/chat/query/stream',
    //       createProxyMiddleware({
    //         target: 'http://localhost:8000',
    //         changeOrigin: true,
    //         ws: true,
    //         selfHandleResponse: false, // This is important for streaming
    //         onProxyRes: (proxyRes, req, res) => {
    //           // Remove buffering headers if present
    //           delete proxyRes.headers['content-encoding'];
    //         },
    //       })
    //     );
    //   };
    //
    // But the most reliable way is to connect directly to the backend for streaming endpoints.

    console.log('Starting EventSource stream with URL:', url);

    // Use EventSource for real-time streaming
    startEventSourceStream(
      url,
      (data) => {
        console.log('EventSource step data:', data);
        // onStep: handle step_start and step_output events
        setMessages((prev) => {
          // Find the last assistant message in progress
          const idx = [...prev].reverse().findIndex(
            m => m.role === 'assistant' && m.thinkingProcessStatus === 'in_progress'
          );
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;

          const updated = [...prev];
          let steps = [...(updated[realIdx].thinkingProcess || [])];
          let content = updated[realIdx].content;

          if (data.type === 'step_start') {
            const newStep = {
              step_id: data.step_id,
              title: data.step_name || data.step_id,
              transcript: [],
              raw_output: null,
              status: 'in_progress',
            };
            steps = [...steps, newStep];
            console.log('Added new step:', newStep);
          } else if (data.type === 'step_output') {
            const stepIdx = steps.findIndex(s => s.step_id === data.step_id);
            if (stepIdx !== -1) {
              const updatedStep = { ...steps[stepIdx] };
              updatedStep.title = data.step_name || updatedStep.title;
              updatedStep.transcript = data.text ? [data.text] : [];
              updatedStep.raw_output = data.raw_output;
              updatedStep.status = 'completed';
              if (data.duration_ms) {
                updatedStep.duration_ms = data.duration_ms;
              }
              steps[stepIdx] = updatedStep;
              console.log('Updated step:', updatedStep);
            }
          }

          updated[realIdx] = {
            ...updated[realIdx],
            thinkingProcess: steps,
            content,
          };
          console.log('Updated assistant message:', updated[realIdx]);
          return updated;
        });
      },
      (data) => {
        console.log('EventSource complete data:', data);
        // onComplete: handle process_complete event
        setMessages((prev) => {
          console.log('Complete - Current messages:', prev);
          const assistantIdx = prev.findIndex(m => m.role === 'assistant' && m.thinkingProcessStatus === 'in_progress');
          console.log('Complete - Assistant message index:', assistantIdx);
          
          if (assistantIdx === -1) {
            console.log('Complete - No assistant message found');
            return prev;
          }
          
          const updated = [...prev];
          let content = data.result && data.result.response ? data.result.response : '';
          
          // Extract visualization data
          const visualization = data.result?.visualization;
          const table = data.result?.table;
          const chart = data.result?.chart;
          
          updated[assistantIdx] = {
            ...updated[assistantIdx],
            thinkingProcessStatus: 'complete',
            content,
            visualization,
            table,
            chart,
          };
          console.log('Complete - Updated assistant message:', updated[assistantIdx]);
          return updated;
        });
        
        // Set latest result for visualizations
        if (data.result) {
          setLatestResult({
            response: data.result.response,
            data: data.result.data,
            visualization: data.result.visualization,
            sources: data.result.sources,
            table: data.result.table,
            chart: data.result.chart
          });
        }
        
        setChatLoading(false);
        
        // Reload conversations to show the new conversation
        if (!currentConversation) {
          loadConversations();
        }
      },
      (err) => {
        console.error('EventSource error:', err);
        setMessages((prev) => {
          const assistantIdx = prev.findIndex(m => m.role === 'assistant' && m.thinkingProcessStatus === 'in_progress');
          if (assistantIdx === -1) return prev;
          
          const updated = [...prev];
          updated[assistantIdx] = {
            ...updated[assistantIdx],
            content: `Sorry, I encountered an error: ${err.message}. Please try again.`,
            thinkingProcessStatus: 'failed'
          };
          return updated;
        });
        setChatLoading(false);
      }
    );
  }, [currentConversation, loadConversations]);

  // Helper: EventSource-based streaming for endpoints that support SSE (GET/text-event-stream)
  function startEventSourceStream(url, onStep, onComplete, onError) {
    console.log('Creating EventSource for URL:', url);
    const es = new window.EventSource(url);
    
    es.onopen = (event) => {
      console.log('EventSource connection opened');
    };
    
    es.onmessage = (event) => {
      console.log('EventSource received message:', event.data);
      try {
        const data = JSON.parse(event.data);
        console.log('Parsed EventSource data:', data);
        
        if (data.type === 'process_complete') {
          console.log('Process complete, closing connection');
          if (onComplete) onComplete(data);
          es.close();
        } else if (data.type === 'step_start' || data.type === 'step_output') {
          if (onStep) onStep(data);
        } else if (data.type === 'process_start') {
          console.log('Process started:', data);
          // Handle process start if needed
        } else {
          console.log('Unknown event type:', data.type);
        }
      } catch (e) {
        console.error('EventSource parse error:', e, 'Raw data:', event.data);
      }
    };
    
    es.onerror = (err) => {
      console.error('EventSource error:', err);
      if (onError) onError(err);
      es.close();
    };
    
    return es;
  }

  // Usage example (for GET endpoints):
  // startEventSourceStream('/api/chat/query/stream', handleStep, handleComplete, handleError);
  // For POST endpoints, use fetch/streaming as before.

  // All useEffect hooks - always called
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qbSuccess = urlParams.get('qb_success');
    const qbError = urlParams.get('qb_error');
    const newToken = urlParams.get('token');

    // Only show OAuth message if there's an actual OAuth callback
    if (qbSuccess === 'true' && (qbError || newToken)) {
      if (newToken && updateToken) {
        console.log('Updating token from OAuth callback');
        updateToken(newToken);
      }

      setOauthMessage({
        type: 'success',
        title: 'QuickBooks Connected!',
        message: 'Your QuickBooks account has been successfully connected.'
      });
      
      setTimeout(() => {
        checkQBConnection();
        setOauthMessage(null);
      }, 3000);
      
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (qbError && qbError !== '') {
      setOauthMessage({
        type: 'error',
        title: 'Connection Failed',
        message: `Failed to connect QuickBooks: ${qbError.replace(/_/g, ' ')}`
      });
      
      setTimeout(() => {
        setOauthMessage(null);
      }, 5000);
      
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [updateToken, checkQBConnection]);

  useEffect(() => {
    if (token && user && isAuthenticated) {
      console.log('User authenticated, loading data...');
      checkQBConnection();
      loadConversations();
    }
  }, [token, user, isAuthenticated, checkQBConnection, loadConversations]);

  // Fallback: Try to load conversations again after a delay if they're still empty
  useEffect(() => {
    if (token && user && isAuthenticated && conversations.length === 0 && !conversationsLoading) {
      console.log('No conversations found, retrying after delay...');
      const timer = setTimeout(() => {
        loadConversations();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [token, user, isAuthenticated, conversations.length, conversationsLoading, loadConversations]);

  // Prevent chat input focus during chat loading only
  useEffect(() => {
    if (chatLoading && chatInputRef.current) {
      // Blur any focused element to prevent blue borders
      if (document.activeElement) {
        document.activeElement.blur();
      }
      
      // Additional prevention - remove any blue borders from all elements
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        if (element.style.borderColor && element.style.borderColor.includes('blue')) {
          element.style.borderColor = 'transparent';
        }
        if (element.style.outlineColor && element.style.outlineColor.includes('blue')) {
          element.style.outlineColor = 'transparent';
        }
      });
    }
  }, [chatLoading]);

  // CONDITIONAL RENDERING ONLY AFTER ALL HOOKS
  if (loading) {
    return (
      <div className="loading-screen">
        <Loader2 size={48} className="spinning" />
        <p>Loading Sterling...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  // Main app UI
  return (
    <div className={`App ${loading ? 'loading no-blue-borders' : ''}`}>
      <Header 
        onGoToHome={handleGoToHome} 
        showSettings={showSettings}
        onCloseSettings={() => setShowSettings(false)}
        onOpenSettings={() => setShowSettings(true)}
        qbConnected={qbConnected}
        qbStatus={qbStatus}
        onQBStatusUpdate={checkQBConnection}
      />
      
      {/* OAuth Status Message */}
      {oauthMessage && (
        <div className={`oauth-message ${oauthMessage.type}`}>
          <div className="oauth-message-content">
            {oauthMessage.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <XCircle size={20} />
            )}
            <div>
              <h4>{oauthMessage.title}</h4>
              <p>{oauthMessage.message}</p>
            </div>
          </div>
        </div>
      )}
      
      <main className="main-content split-layout">
        {/* Chat view - full conversation interface */}
        <div className="app-layout">
          {/* Conversation History Sidebar */}
          {showConversationHistory && (
            <ConversationHistory
              conversations={conversations}
              currentConversationId={currentConversation}
              onSelectConversation={handleSelectConversation}
              onDeleteConversation={handleDeleteConversation}
              onNewChat={handleNewChat}
              loading={conversationsLoading}
            />
          )}
          
          {/* Main Chat and Results Area */}
          <div className="main-chat-area">
            <div className="chat-panel">
              <div className="chat-header">
                <div className="chat-title">
                  <button 
                    className="toggle-history-btn"
                    onClick={() => setShowConversationHistory(!showConversationHistory)}
                    title={showConversationHistory ? "Hide conversations" : "Show conversations"}
                  >
                    {showConversationHistory ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
                  </button>
                  <span>Sterling Chat</span>
                </div>
                
                {/* QuickBooks Connection Indicator */}
                <div className="qb-connection-indicator">
                  {qbChecking ? (
                    <div className="qb-status-checking">
                      <Loader2 size={16} className="spinning" />
                      <span>Checking QuickBooks...</span>
                    </div>
                  ) : qbConnected ? (
                    <div className="qb-status-connected">
                      <CheckCircle size={16} />
                      <span>QuickBooks Connected</span>
                      {qbStatus?.company_name && (
                        <span className="company-name">({qbStatus.company_name})</span>
                      )}
                    </div>
                  ) : (
                    <div className="qb-connection-actions">
                      <button 
                        className="qb-connect-cta"
                        onClick={() => setShowSettings(true)}
                        disabled={qbChecking}
                      >
                        <BookOpen size={16} />
                        Connect QuickBooks
                      </button>
                      {qbStatus?.error && (
                        <button 
                          className="qb-retry-btn"
                          onClick={() => checkQBConnection()}
                          title="Retry connection"
                          disabled={qbChecking}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <ChatInterface 
                messages={messages}
                onSendMessage={handleSendMessage}
                loading={chatLoading}
                compact={true}
                qbConnected={qbConnected}
                thinkingProcesses={messages.filter(msg => msg.thinkingProcess).map(msg => msg.thinkingProcess)}
                currentThinkingProcess={messages.filter(msg => msg.thinkingProcess).map(msg => msg.thinkingProcess).find(process => process.status === 'in_progress')}
                onProcessComplete={() => {}} // No longer needed here
                ref={chatInputRef}
              />
            </div>
            
            <div className="results-panel">
              <ResultsPanel result={latestResult} qbConnected={qbConnected} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Root App component - just provides context
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// Move this function outside the component
function testEventSourceStream() {
  const es = new window.EventSource('http://localhost:8000/test-stream');
  es.onmessage = (event) => {
    console.log('EventSource event:', event.data);
  };
  es.onerror = (err) => {
    console.error('EventSource error:', err);
    es.close();
  };
}

export { testEventSourceStream };

export default App;